import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { api } from "@/lib/api"
import { manualAssignmentRequest } from "./distributionUi"

interface AssignmentRecord {
  id: string
  assignmentVersion: number
  pseApplicationId: number
}

/** Explicit reassignment is separate from distribution of untouched new applications. */
export function ManualAssignment({ records, staff, onAssigned }: {
  records: readonly AssignmentRecord[]
  staff: readonly { id: string; displayName: string }[]
  onAssigned: () => Promise<void>
}) {
  const [target, setTarget] = useState("")
  const [pending, setPending] = useState<{ request: ReturnType<typeof manualAssignmentRequest>; name: string; applications: number[] } | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const review = () => {
    const name = target === "__unassigned" ? "Unassigned" : staff.find(item => item.id === target)?.displayName
    if (!name || records.length === 0 || records.length > 200) return
    setPending({ request: manualAssignmentRequest(records, target === "__unassigned" ? null : target), name, applications: records.map(record => record.pseApplicationId) })
    setMessage(null)
  }
  const apply = async () => {
    if (!pending) return
    setSaving(true)
    try {
      const result = await api.patch<{ updatedCount: number }>("/api/pse-online/records/assignment", pending.request)
      setMessage(`Changed ${result.updatedCount} assignment(s).`)
      setPending(null)
      await onAssigned()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Assignment failed. Reload and review the selection again.")
      setPending(null)
    } finally {
      setSaving(false)
    }
  }
  return <div className="space-y-2 rounded border p-3">
    <p className="text-sm font-medium">Assign selected records</p>
    <p className="text-xs text-muted-foreground">Explicitly assign or reassign the selected visible records. Existing matching decisions stay intact; records with created CMS files cannot be reassigned here.</p>
    <div className="flex flex-wrap gap-2">
      <Select value={target} onValueChange={setTarget} disabled={saving}>
        <SelectTrigger aria-label="Selected records assignment target" className="w-[240px]"><SelectValue placeholder="Choose intake staff" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__unassigned">Unassigned</SelectItem>
          {staff.map(item => <SelectItem key={item.id} value={item.id}>{item.displayName}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={review} disabled={saving || !target || records.length === 0 || records.length > 200}>Review assignment ({records.length})</Button>
    </div>
    {message && <p role="status" className="text-sm">{message}</p>}
    <AlertDialog open={pending !== null} onOpenChange={open => { if (!open && !saving) setPending(null) }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm selected assignments</AlertDialogTitle>
          <AlertDialogDescription>Set exactly {pending?.applications.length ?? 0} selected record(s) to {pending?.name}. This may replace their existing assigned staff. Concurrent ownership changes reject the entire request.</AlertDialogDescription>
        </AlertDialogHeader>
        <p className="max-h-40 overflow-auto text-xs">Applications: {pending?.applications.join(", ")}</p>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={saving} onClick={event => { event.preventDefault(); void apply() }}>{saving ? "Assigning…" : "Confirm assignment"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
}
